import { Avatar, Box, Button, IconButton, Typography } from "@mui/material";
import { FavoriteBorder, ChatBubbleOutline, SendOutlined, MoreHoriz, Repeat, PhotoCameraOutlined } from "@mui/icons-material";
import { getMediaKind } from "../util";

interface Props {
  media?: string[];
  caption?: string;
  username?: string;
  avatarUrl?: string;
  likes?: number;
  comments?: number;
  shares?: number;
}

export default function InstagramReelsPreview({
  media = [],
  caption,
  username = "Your Account",
  avatarUrl,
  likes = 43300,
  comments = 96,
  shares = 1467
}: Props) {
  const first = media[0];
  const isVideo = first ? getMediaKind(first) === "video" : false;

  const fmt = (n: number) =>
    n >= 1_000_000 ? `${(n / 1_000_000).toFixed(1)}M` :
      n >= 1_000 ? `${(n / 1_000).toFixed(1)}K` : `${n}`;

  return (
    <Box sx={styles.root}>
      {first ? (
        <Box
          component={isVideo ? "video" : "img"}
          src={first}
          autoPlay={isVideo ? true : undefined}
          muted={isVideo ? true : undefined}
          loop={isVideo ? true : undefined}
          playsInline={isVideo ? true : undefined}
          sx={styles.media}
        />
      ) : (
        <Box sx={styles.noMedia}>
          <Typography variant="body2">No media for Reels</Typography>
        </Box>
      )}

      <Box sx={styles.topGradient} />
      <Box sx={styles.bottomGradient} />

      <IconButton size="small" sx={styles.cameraBtn} aria-label="camera">
        <PhotoCameraOutlined />
      </IconButton>

      <Box sx={styles.actions}>
        <Box sx={styles.action}>
          <IconButton size="small" sx={styles.actionBtn}><FavoriteBorder /></IconButton>
          <Typography sx={styles.actionCount}>{fmt(likes)}</Typography>
        </Box>
        <Box sx={styles.action}>
          <IconButton size="small" sx={styles.actionBtn}><ChatBubbleOutline /></IconButton>
          <Typography sx={styles.actionCount}>{fmt(comments)}</Typography>
        </Box>
        <Box sx={styles.action}>
          <IconButton size="small" sx={styles.actionBtn}><Repeat /></IconButton>
          <Typography sx={styles.actionCount}>{fmt(shares)}</Typography>
        </Box>
        <Box sx={styles.action}>
          <IconButton size="small" sx={styles.actionBtn}><SendOutlined /></IconButton>
        </Box>
        <Box sx={styles.action}>
          <IconButton size="small" sx={styles.actionBtn}><MoreHoriz /></IconButton>
        </Box>
      </Box>

      <Box sx={styles.bottomRow}>
        <Box sx={styles.userLine}>
          <Avatar src={avatarUrl} sx={styles.avatar}>
            {username?.[0]?.toUpperCase() ?? "U"}
          </Avatar>
          <Typography sx={styles.username}>{username}</Typography>
          <Button size="small" variant="outlined" sx={styles.followBtn}>
            Follow
          </Button>
          <Box sx={{ flexGrow: 1 }} />
        </Box>

        {caption?.trim() ? (
          <Typography sx={styles.caption} title={caption}>
            {caption}
          </Typography>
        ) : null}
      </Box>
    </Box>
  );
}

const styles = {
  root: {
    position: "relative",
    width: "100%",
    maxWidth: 360,
    aspectRatio: "9 / 16",
    bgcolor: "black",
    borderRadius: 2,
    overflow: "hidden",
    color: "common.white",
    boxShadow: 1
  },
  media: {
    width: "100%",
    height: "100%",
    objectFit: "contain",
    display: "block",
    backgroundColor: "black"
  },
  noMedia: {
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "grey.500"
  },
  topGradient: {
    pointerEvents: "none",
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: 120,
    background:
      "linear-gradient(180deg, rgba(0,0,0,0.65) 0%, rgba(0,0,0,0.35) 50%, rgba(0,0,0,0) 100%)"
  },
  bottomGradient: {
    pointerEvents: "none",
    position: "absolute",
    left: 0,
    right: 0,
    bottom: 0,
    height: 200,
    background:
      "linear-gradient(0deg, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.35) 50%, rgba(0,0,0,0) 100%)"
  },
  cameraBtn: {
    position: "absolute",
    top: 10,
    right: 10,
    color: "common.white",
    bgcolor: "rgba(0,0,0,0.35)",
    "&:hover": { bgcolor: "rgba(0,0,0,0.55)" }
  },
  actions: {
    position: "absolute",
    right: 8,
    bottom: 24,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 1
  },
  action: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 0.25
  },
  actionBtn: {
    color: "common.white",
    bgcolor: "rgba(0,0,0,0.35)",
    "&:hover": { bgcolor: "rgba(0,0,0,0.55)" }
  },
  actionCount: {
    fontSize: "0.8rem",
    fontWeight: 600
  },
  bottomRow: {
    position: "absolute",
    left: 12,
    right: 12,
    bottom: 12
  },
  userLine: {
    display: "flex",
    alignItems: "center",
    gap: 1,
    mb: 0.5
  },
  avatar: { width: 28, height: 28 },
  username: { fontWeight: 700, fontSize: "0.95rem" },
  followBtn: {
    textTransform: "none",
    color: "common.white",
    borderColor: "rgba(255,255,255,0.5)",
    borderRadius: 2,
    lineHeight: 1.2,
    px: 1.25,
    py: 0.25,
    "&:hover": { borderColor: "rgba(255,255,255,0.8)" }
  },
  caption: {
    fontSize: "0.9rem",
    opacity: 0.95,
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
    textOverflow: "ellipsis",
    pr: 5
  }
};
